import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDraggableDrawer } from '../../../../hooks/useDraggableDrawer';
import { DrawerContainer } from './DrawerContainer';
import { HandleBar } from './HandleBar';
import { DRAWER_HEIGHTS } from '../../constants';

/**
 * A draggable drawer.
 *
 * @param { DraggableDrawerProps } props - The properties for the DraggableDrawer component.
 * - `children`: Content to display inside the drawer.
 * - `drawerState`: The state of the mobile drawer.
 * - `setDrawerState`: Function to update drawerState.
 * @param { React.ForwardedRef<HTMLDivElement> } ref - Reference to the drawer element.
 *
 * @returns {JSX.Element} The DraggableDrawer component.
 */

const DraggableDrawer = ({
  children,
  drawerState,
  setDrawerState,
  onClose,
}) => {
  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dragDistance,
  } = useDraggableDrawer(drawerState, setDrawerState, onClose);
  const triggerRef = useRef(null);
  const [currentHeight, setCurrentHeight] = useState(DRAWER_HEIGHTS[drawerState]);

  const containerClasses = useMemo(() => classNames(
    drawerState === 'OPEN' ? 'sui-fixed' : 'sui-absolute',
    { 'sui-shadow-[0_-1px_6px_2px_rgba(0,0,0,0.3)]': drawerState !== 'CLOSED' }
  ), [drawerState]);

  const cssDrawerHeightValue = useMemo(() => {
    if (drawerState === 'OPEN' && !dragDistance) {
      return 'calc(100% - 1em)';
    }
    const viewportHeight = window.innerHeight;
    const direction = drawerState === 'OPEN' || drawerState === 'MINIMIZED' ? -1 : 1;
    const heightPercentage = dragDistance && Math.abs((dragDistance / viewportHeight) * 100 + 100 * direction);
    const computedHeight = heightPercentage ? direction * heightPercentage + currentHeight : 0;
    // eslint-disable-next-line max-len,no-nested-ternary
    const clampedHeightValue = computedHeight > DRAWER_HEIGHTS.OPEN ? DRAWER_HEIGHTS.OPEN : computedHeight < DRAWER_HEIGHTS.CLOSED ? DRAWER_HEIGHTS.CLOSED : computedHeight;
    return `calc(${clampedHeightValue || currentHeight}vh)`;
  }, [currentHeight, drawerState, dragDistance]);
  const dynamicStyles = useMemo(() => ({
    height: cssDrawerHeightValue,
    ...(drawerState === 'MINIMIZED' && !dragDistance ? { maxHeight: 'fit-content' } : {})
  }), [cssDrawerHeightValue, drawerState]);

  const headerClasses = 'drawer-header-with-handlebar-div sui-bg-primary sui-sticky sui-top-0 sui-bottom-0 sui-z-200';

  useEffect(() => {
    setCurrentHeight(DRAWER_HEIGHTS[drawerState]);
  }, [drawerState, dragDistance]);

  useEffect(() => {
    const handleInteraction = (event, elemRef) => {
      if (elemRef.current && elemRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };

    if (document !== 'undefined') {
      document.addEventListener('touchstart', (event) => handleInteraction(event, triggerRef), { passive: false });
    }

    return (() => {
      if (document !== 'undefined') {
        document.removeEventListener('touchstart', (event) => handleInteraction(event, triggerRef));
      }
    });
  }, []);

  return (
    <DrawerContainer
      className={containerClasses}
      dynamicStyles={dynamicStyles}
    >
      <div className={headerClasses}>
        <HandleBar
          handlePointerDown={handlePointerDown}
          handlePointerMove={handlePointerMove}
          handlePointerUp={handlePointerUp}
          drawerState={drawerState}
          ref={triggerRef}
        />
      </div>
      {children}
    </DrawerContainer>
  );
};

DraggableDrawer.displayName = 'DraggableDrawer';

DraggableDrawer.propTypes = {
  children: PropTypes.node.isRequired,
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

DraggableDrawer.defaultProps = {
  onClose: () => {},
};

export { DraggableDrawer };
